// Database Service - Uses Supabase with real-time sync, falls back to mockData
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { mockApi, type User, type Sale, type Stall, type SaleInput, type InventoryItem as Item } from './mockData';
import { syncOfflineUserProfile, upsertOfflineCredentialFromPassword, removeOfflineCredential } from '../utils/offlineCredentials';
import { derivePasswordHash } from '../utils/passwordUtils';
import { computeHubStock, summarizeStallReturnLedger } from '../utils/stockReplay';
import type { RealtimeChannel } from '@supabase/supabase-js';

// Export interfaces for compatibility
export type { User, Sale, Stall, Item, SaleInput };

const SALES_PAGE_SIZE = 1000;

export type SalesAggregates = {
  byItem: Record<number, number>;
  byItemStall: Record<string, number>;
};

type SalesQuantityRow = {
  item_id: number;
  stall_id: number | null;
  quantity_sold: number;
};

const buildSalesAggregates = (rows: SalesQuantityRow[]): SalesAggregates => {
  const byItem: Record<number, number> = {};
  const byItemStall: Record<string, number> = {};

  for (const row of rows) {
    const itemId = Number(row.item_id);
    const qty = Number(row.quantity_sold) || 0;
    byItem[itemId] = (byItem[itemId] || 0) + qty;
    const stallKey = `${itemId}-${row.stall_id ?? 'null'}`;
    byItemStall[stallKey] = (byItemStall[stallKey] || 0) + qty;
  }

  return { byItem, byItemStall };
};

/** Units sold at one stall for one item (paginated — avoids the 1000-row default). */
const sumSoldAtStall = async (itemId: number, stallId: number): Promise<number> => {
  let total = 0;
  let from = 0;

  while (true) {
    const { data, error } = await (supabase as any)
      .from('sales')
      .select('quantity_sold')
      .eq('item_id', itemId)
      .eq('stall_id', stallId)
      .order('sale_id', { ascending: true })
      .range(from, from + SALES_PAGE_SIZE - 1);

    if (error) throw error;
    const page = data || [];
    for (const row of page) total += Number(row.quantity_sold) || 0;
    if (page.length < SALES_PAGE_SIZE) break;
    from += SALES_PAGE_SIZE;
  }

  return total;
};

const fetchAllSalesQuantityRows = async (): Promise<SalesQuantityRow[]> => {
  const rows: SalesQuantityRow[] = [];
  let from = 0;

  while (true) {
    const { data, error } = await (supabase as any)
      .from('sales')
      .select('item_id, stall_id, quantity_sold')
      .order('sale_id', { ascending: true })
      .range(from, from + SALES_PAGE_SIZE - 1);

    if (error) throw error;

    const page = (data || []) as SalesQuantityRow[];
    rows.push(...page);

    if (page.length < SALES_PAGE_SIZE) break;
    from += SALES_PAGE_SIZE;
  }

  return rows;
};

/** Per-batch slices for multi-batch stall→hub returns (parent withdrawal rows). */
const fetchWithdrawalBatchParts = async (
  withdrawalIds: number[]
): Promise<Array<{ withdrawal_id: number; distribution_id: number; quantity_withdrawn: number }>> => {
  const ids = Array.from(new Set(withdrawalIds.map(Number).filter((id) => Number.isFinite(id) && id > 0)));
  if (ids.length === 0) return [];

  const parts: Array<{ withdrawal_id: number; distribution_id: number; quantity_withdrawn: number }> = [];
  const CHUNK = 200;

  for (let i = 0; i < ids.length; i += CHUNK) {
    const chunk = ids.slice(i, i + CHUNK);
    let from = 0;
    while (true) {
      const { data, error } = await (supabase as any)
        .from('stock_withdrawal_batches')
        .select('withdrawal_id, distribution_id, quantity_withdrawn')
        .in('withdrawal_id', chunk)
        .order('batch_link_id', { ascending: true })
        .range(from, from + SALES_PAGE_SIZE - 1);

      if (error) {
        // Table not installed yet — treat as no parts (legacy multi-row history).
        if (String(error.message || '').toLowerCase().includes('stock_withdrawal_batches')) {
          return [];
        }
        throw error;
      }

      const page = data || [];
      for (const row of page) {
        parts.push({
          withdrawal_id: Number(row.withdrawal_id),
          distribution_id: Number(row.distribution_id),
          quantity_withdrawn: Number(row.quantity_withdrawn) || 0
        });
      }
      if (page.length < SALES_PAGE_SIZE) break;
      from += SALES_PAGE_SIZE;
    }
  }

  return parts;
};

const mapSaleRow = (sale: any): Sale => {
  const credit = Array.isArray(sale.credit_sales) ? sale.credit_sales[0] : sale.credit_sales;

  return {
    ...sale,
    recorded_by_name: sale.users?.full_name || 'Unknown',
    stall_name: sale.stalls?.stall_name || 'Unknown',
    item_name: sale.items?.item_name || 'Unknown',
    category: sale.items?.category || 'Unknown',
    customer_name: credit?.customer_name ?? sale.customer_name,
    customer_contact: credit?.customer_contact ?? sale.customer_contact,
    payment_status: credit?.payment_status ?? sale.payment_status,
    balance_due: credit?.balance_due ?? sale.balance_due,
    amount_paid: credit?.amount_paid ?? sale.amount_paid,
    due_date: credit?.due_date ?? sale.due_date,
    notes: credit?.notes ?? sale.notes
  };
};

// Real-time subscription channels
let inventoryChannel: RealtimeChannel | null = null;
let salesChannel: RealtimeChannel | null = null;
let usersChannel: RealtimeChannel | null = null;

// Setup real-time subscriptions.
//
// These only announce that something changed. Each page reloads its own data
// through the normal paginated API in response. Fetching here as well meant an
// unpaginated read of the whole sales table on every single sale — which
// Supabase silently truncates at 1000 rows — and every listener discarded the
// result anyway.
export const setupRealtimeSubscriptions = () => {
  if (!isSupabaseConfigured()) {
    console.log('📝 Supabase not configured, using polling instead');
    return () => { }; // Return cleanup function
  }

  // Subscribe to inventory changes
  inventoryChannel = (supabase as any)
    .channel('inventory-changes')
    .on('postgres_changes',
      { event: '*', schema: 'public', table: 'items' },
      () => {
        console.log('📡 Real-time: Inventory changed');
        window.dispatchEvent(new Event('inventory-updated'));
      }
    )
    .subscribe();

  // Subscribe to sales changes
  salesChannel = (supabase as any)
    .channel('sales-changes')
    .on('postgres_changes',
      { event: '*', schema: 'public', table: 'sales' },
      () => {
        console.log('📡 Real-time: Sales changed');
        window.dispatchEvent(new Event('sales-updated'));
      }
    )
    .subscribe();

  // Subscribe to users changes
  usersChannel = (supabase as any)
    .channel('users-changes')
    .on('postgres_changes',
      { event: '*', schema: 'public', table: 'users' },
      () => {
        console.log('📡 Real-time: Users changed');
        window.dispatchEvent(new Event('users-updated'));
      }
    )
    .subscribe();

  // Cleanup function
  return () => {
    inventoryChannel?.unsubscribe();
    salesChannel?.unsubscribe();
    usersChannel?.unsubscribe();
  };
};

// Detects "function does not exist" errors so we can fall back gracefully
// when the atomic SQL functions haven't been installed in Supabase yet.
const isMissingFunctionError = (error: any): boolean => {
  if (!error) return false;
  const code = error.code || '';
  const message = String(error.message || '');
  return (
    code === 'PGRST202' ||
    code === '42883' ||
    code === '42725' || // ambiguous function (wrong number of args)
    /could not find the function|function .* does not exist|wrong number of arguments/i.test(message)
  );
};

// Postgres / PostgREST wrap the useful sentence in "ERROR: ...". The shop
// should see "Only 4 left at this stall", not a stack of SQLSTATE codes.
const friendlyDbError = (error: unknown): string => {
  const raw = error && typeof error === 'object' && 'message' in (error as any)
    ? String((error as any).message || '')
    : String(error ?? '');
  const cleaned = raw.replace(/^.*ERROR:\s*/i, '').split('\n')[0].trim();
  return cleaned || 'The database rejected this change.';
};

// Recompute an item's denormalized totals from the history tables and persist
// them. Single source of truth for stock math:
//   total_added     = SUM(stock_additions)
//   total_allocated = SUM(stock_distribution)
//   current_stock   = chronological replay with floor-at-zero on deductions
//                     (historical over-withdrawals stay in history but do not
//                     create a deficit that absorbs future additions)
// Recompute item totals from full history.
// Always uses client-side hub math (including FIFO stall→hub returns) so
// over-allocated items do not stay stuck at current_stock = 0.
const recomputeItemTotals = async (itemId: number): Promise<Item> => {
  const { data: item, error: itemError } = await (supabase as any)
    .from('items')
    .select('*')
    .eq('item_id', itemId)
    .single();

  if (itemError || !item) {
    throw new Error('Item not found.');
  }

  // Paged reads. A plain select stops at 1000 rows without reporting an error,
  // so a long-lived item's history would look complete while missing its
  // newest rows, and this function writes that result straight into items.
  const [additionsRes, distributionsRes, centralSalesRes, withdrawalsRes] = await Promise.all([
    fetchAllRows(
      'stock_additions',
      'quantity_added, date_added, addition_id',
      'addition_id',
      (q: any) => q.eq('item_id', itemId)
    ),
    fetchAllRows(
      'stock_distribution',
      'quantity_allocated, date_distributed, distribution_id, stall_id',
      'distribution_id',
      (q: any) => q.eq('item_id', itemId)
    ),
    fetchAllRows(
      'sales',
      'quantity_sold, date_time, sale_id',
      'sale_id',
      (q: any) => q.eq('item_id', itemId).is('stall_id', null)
    ),
    fetchAllRows(
      'stock_withdrawals',
      'quantity_withdrawn, date_withdrawn, withdrawal_id, stall_id, distribution_id',
      'withdrawal_id',
      (q: any) => q.eq('item_id', itemId)
    )
  ]);

  // CRITICAL: never recompute from partial history. A failed query would read
  // as an empty list (sum 0) and corrupt the items table with wrong totals.
  if (additionsRes.error || distributionsRes.error || centralSalesRes.error || withdrawalsRes.error) {
    throw new Error('Failed to load full stock history; refusing to recompute totals from partial data.');
  }

  const additions = additionsRes.data || [];
  const distributions = distributionsRes.data || [];
  const centralSales = centralSalesRes.data || [];
  const withdrawals = withdrawalsRes.data || [];
  const withdrawalBatchParts = await fetchWithdrawalBatchParts(
    withdrawals.map((w: any) => Number(w.withdrawal_id))
  );

  const totalAdded = additions.reduce((sum: number, a: any) => sum + (a.quantity_added || 0), 0);
  const totalAllocated = distributions.reduce((sum: number, d: any) => sum + (d.quantity_allocated || 0), 0);
  const currentStock = computeHubStock({
    initialStock: item.initial_stock || 0,
    additions,
    distributions,
    withdrawals,
    centralSales,
    withdrawalBatchParts
  });

  const { data: updated, error: updateError } = await (supabase as any)
    .from('items')
    .update({
      total_added: totalAdded,
      total_allocated: totalAllocated,
      current_stock: currentStock
    })
    .eq('item_id', itemId)
    .select()
    .single();

  if (updateError) throw updateError;
  return updated as Item;
};

// A failed read must never fall back to mock data. Returning the empty mock
// inventory made a dropped request look like the shop had lost every item, and
// the mock stall/user lists are stale, so a blip could hide a real stall. The
// caller shows this message and keeps the last good numbers on screen.
const readFailed = (what: string, cause: unknown): Error => {
  const detail = cause instanceof Error ? cause.message : String(cause ?? 'unknown error');
  return new Error(
    `Couldn't load ${what}. Check your connection and try again. (${detail})`
  );
};

// Supabase caps a single response at 1000 rows, so anything that could exceed
// that must be paged. Pages are ordered by primary key; without a stable order
// rows could repeat or be skipped between pages and the stock figures would be
// silently wrong.
const SUPABASE_PAGE_SIZE = 1000;

const fetchAllRows = async (
  table: string,
  columns: string,
  orderBy: string,
  applyFilters: (query: any) => any
): Promise<{ data: any[] | null; error: any }> => {
  const rows: any[] = [];
  for (let from = 0; ; from += SUPABASE_PAGE_SIZE) {
    const { data, error } = await applyFilters((supabase as any).from(table).select(columns))
      .order(orderBy, { ascending: true })
      .range(from, from + SUPABASE_PAGE_SIZE - 1);

    if (error) return { data: null, error };

    const page = data || [];
    rows.push(...page);
    if (page.length < SUPABASE_PAGE_SIZE) return { data: rows, error: null };
  }
};

const groupByItemId = (rows: any[]): Map<number, any[]> => {
  const grouped = new Map<number, any[]>();
  for (const row of rows) {
    const key = Number(row.item_id);
    const bucket = grouped.get(key);
    if (bucket) bucket.push(row);
    else grouped.set(key, [row]);
  }
  return grouped;
};

// Referenced records block a delete at the database level. Counting them first
// lets us say which records are in the way instead of surfacing a raw foreign
// key error to the user.
const countRows = async (table: string, column: string, value: number): Promise<number> => {
  const { count, error } = await (supabase as any)
    .from(table)
    .select('*', { count: 'exact', head: true })
    .eq(column, value);
  if (error) throw error;
  return count || 0;
};

const describeCounts = (parts: Array<{ label: string; count: number }>): string => {
  const present = parts.filter((p) => p.count > 0).map((p) => `${p.count} ${p.label}`);
  if (present.length === 0) return 'no records';
  if (present.length === 1) return present[0];
  return `${present.slice(0, -1).join(', ')} and ${present[present.length - 1]}`;
};

const countUserActivity = async (userId: number) => {
  const [sales, distributions, withdrawals, additions] = await Promise.all([
    countRows('sales', 'recorded_by', userId),
    countRows('stock_distribution', 'distributed_by', userId),
    countRows('stock_withdrawals', 'withdrawn_by', userId),
    countRows('stock_additions', 'added_by', userId)
  ]);
  const parts = [
    { label: 'sale(s)', count: sales },
    { label: 'distribution(s)', count: distributions },
    { label: 'withdrawal(s)', count: withdrawals },
    { label: 'stock addition(s)', count: additions }
  ];
  return { parts, total: sales + distributions + withdrawals + additions };
};

const countStallActivity = async (stallId: number) => {
  const [sales, distributions, withdrawals, staff] = await Promise.all([
    countRows('sales', 'stall_id', stallId),
    countRows('stock_distribution', 'stall_id', stallId),
    countRows('stock_withdrawals', 'stall_id', stallId),
    countRows('users', 'stall_id', stallId)
  ]);
  const parts = [
    { label: 'sale(s)', count: sales },
    { label: 'distribution(s)', count: distributions },
    { label: 'withdrawal(s)', count: withdrawals },
    { label: 'assigned user(s)', count: staff }
  ];
  return { parts, total: sales + distributions + withdrawals + staff };
};

const getCurrentUserId = (): number => {
  try {
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    return currentUser.user_id || 1;
  } catch {
    return 1;
  }
};

// Database API - Uses Supabase if configured, otherwise mockData
export const dbApi = {
  // Users
  getUsers: async () => {
    if (!isSupabaseConfigured()) {
      return mockApi.getUsers();
    }

    try {
      const { data, error } = await (supabase as any)
        .from('users')
        .select(
          'user_id, username, full_name, role, stall_id, status, created_date, phone_number, email, recovery_hint, secret_word'
        )
        .order('created_date', { ascending: false });

      if (error) throw error;

      // Profile-only offline sync — never copy password_hash into localStorage.
      if (data) {
        data.forEach((user: any) => {
          syncOfflineUserProfile({
            user_id: user.user_id,
            username: user.username,
            full_name: user.full_name,
            role: user.role,
            stall_id: user.stall_id,
            status: user.status,
            created_date: user.created_date,
            phone_number: user.phone_number ?? null,
            email: user.email ?? null,
            secret_word: user.secret_word ?? null,
            recovery_hint: user.recovery_hint ?? null,
          });
        });
      }

      return { users: data as User[] };
    } catch (error) {
      console.error('Error fetching users:', error);
      throw readFailed('users', error);
    }
  },

  createUser: async (userData: any) => {
    if (!isSupabaseConfigured()) {
      return mockApi.createUser(userData as any);
    }

    try {
      // Map password to password_hash for Supabase
      const insertData = { ...userData };
      if (insertData.password) {
        // Generate the verifier used by the offline auth system
        insertData.password_hash = await derivePasswordHash(insertData.username, insertData.password);
        delete insertData.password;
      }

      console.log('[Create User] Inserting user into Supabase:', { ...insertData, password_hash: '***' });

      const { data, error } = await (supabase as any)
        .from('users')
        .insert([insertData])
        .select()
        .single();

      if (error) {
        console.error('[Create User] Supabase error:', error);
        throw error;
      }

      if (data) {
        syncOfflineUserProfile({
          user_id: data.user_id,
          username: data.username,
          full_name: data.full_name,
          role: data.role,
          stall_id: data.stall_id,
          status: data.status,
          created_date: data.created_date,
          phone_number: data.phone_number ?? null,
          email: data.email ?? null,
          secret_word: data.secret_word ?? null,
        });
        if (userData.password) {
          await upsertOfflineCredentialFromPassword(
            {
              user_id: data.user_id,
              username: data.username,
              full_name: data.full_name,
              role: data.role,
              stall_id: data.stall_id,
              status: data.status,
              created_date: data.created_date,
              phone_number: data.phone_number ?? null,
              email: data.email ?? null,
            },
            userData.password,
            undefined,
            'server'
          );
        }
      }

      return { user: data as User };
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  },

  updateUser: async (userId: number, userData: Partial<User>) => {
    if (!isSupabaseConfigured()) {
      return mockApi.updateUser(userId, userData as any);
    }

    try {
      const plaintextPassword = (userData as any).password as string | undefined;
      const updateData = { ...userData };

      // Handle password update if provided
      if ((updateData as any).password) {
        // We need the username to derive the hash
        const { data: userRecord } = await (supabase as any)
          .from('users')
          .select('username')
          .eq('user_id', userId)
          .single();

        if (userRecord?.username) {
          console.log(`[Update User] Derived password hash for ${userRecord.username}`);
          updateData.password_hash = await derivePasswordHash(userRecord.username, (updateData as any).password);
        } else {
          console.warn('[Update User] Could not fetch username for hashing. Password update skipped.');
        }
        delete (updateData as any).password;
      }

      const { data, error } = await (supabase as any)
        .from('users')
        .update(updateData)
        .eq('user_id', userId)
        .select()
        .single();

      if (error) throw error;

      if (data) {
        syncOfflineUserProfile({
          user_id: data.user_id,
          username: data.username,
          full_name: data.full_name,
          role: data.role,
          stall_id: data.stall_id,
          status: data.status,
          created_date: data.created_date,
          phone_number: data.phone_number ?? null,
          email: data.email ?? null,
          secret_word: data.secret_word ?? null,
        });
        if (plaintextPassword) {
          await upsertOfflineCredentialFromPassword(
            {
              user_id: data.user_id,
              username: data.username,
              full_name: data.full_name,
              role: data.role,
              stall_id: data.stall_id,
              status: data.status,
              created_date: data.created_date,
              phone_number: data.phone_number ?? null,
              email: data.email ?? null,
            },
            plaintextPassword,
            undefined,
            'server'
          );
        }
      }

      return { user: data as User };
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  },

  deleteUser: async (userId: number) => {
    if (!isSupabaseConfigured()) {
      return mockApi.deleteUser(userId);
    }

    try {
      const { data: target, error: targetError } = await (supabase as any)
        .from('users')
        .select('user_id, username, role')
        .eq('user_id', userId)
        .single();

      if (targetError || !target) throw new Error('User not found');
      if (target.role === 'admin') {
        throw new Error('Admin accounts cannot be deleted. Set the account inactive instead.');
      }

      // Sales, distributions and withdrawals all record who performed them and
      // the database will not release a user who appears in any of them. Say so
      // plainly instead of surfacing a foreign key error.
      const activity = await countUserActivity(userId);
      if (activity.total > 0) {
        throw new Error(
          `This user can't be deleted because their name is attached to ${describeCounts(activity.parts)}. ` +
          `Removing them would break those records. Set their status to inactive instead — they keep their history but can no longer sign in.`
        );
      }

      const { error } = await (supabase as any)
        .from('users')
        .delete()
        .eq('user_id', userId);

      if (error) throw error;
      if (target.username) removeOfflineCredential(target.username);
      return { success: true };
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  },

  deleteItem: async (itemId: number) => {
    if (!isSupabaseConfigured()) {
      return mockApi.deleteItem(itemId);
    }

    try {
      // Deleting an item cascades through its sales, so it would silently erase
      // recorded revenue. Items that were never sold can still be removed.
      const { count: soldCount, error: soldError } = await (supabase as any)
        .from('sales')
        .select('*', { count: 'exact', head: true })
        .eq('item_id', itemId);

      if (soldError) throw soldError;

      if (soldCount && soldCount > 0) {
        throw new Error(
          `This item can't be deleted because it has ${soldCount} recorded sale(s). ` +
          `Deleting it would erase that revenue from your reports. Withdraw the remaining stock instead so the item shows zero.`
        );
      }

      const { error } = await (supabase as any)
        .from('items')
        .delete()
        .eq('item_id', itemId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error deleting item:', error);
      throw error;
    }
  },

  getStockAdditions: async (itemId: number) => {
    if (!isSupabaseConfigured()) {
      return { additions: [] };
    }

    try {
      const { data, error } = await (supabase as any)
        .from('stock_additions')
        .select('*')
        .eq('item_id', itemId)
        .order('date_added', { ascending: false });

      if (error) throw error;
      return { additions: data || [] };
    } catch (error) {
      console.error('Error fetching stock additions:', error);
      throw error;
    }
  },

  deleteStockAddition: async (additionId: number) => {
    if (!isSupabaseConfigured()) {
      return { success: true };
    }

    try {
      const { data: addition, error: fetchError } = await (supabase as any)
        .from('stock_additions')
        .select('*')
        .eq('addition_id', additionId)
        .single();

      if (fetchError || !addition) throw new Error('Stock addition record not found');

      const { item_id } = addition;

      const { error: deleteError } = await (supabase as any)
        .from('stock_additions')
        .delete()
        .eq('addition_id', additionId);

      if (deleteError) throw deleteError;

      // Recompute totals from history (includes central sales + withdrawals)
      await recomputeItemTotals(item_id);

      return { success: true };
    } catch (error) {
      console.error('Error deleting stock addition:', error);
      throw error;
    }
  },

  getStockWithdrawals: async (itemId: number) => {
    if (!isSupabaseConfigured()) {
      return { withdrawals: [] };
    }

    try {
      const { data: withdrawals, error } = await (supabase as any)
        .from('stock_withdrawals')
        .select(`
          *,
          stalls:stall_id(stall_id, stall_name)
        `)
        .eq('item_id', itemId)
        .order('date_withdrawn', { ascending: false });

      if (error) throw error;
      return { withdrawals: withdrawals || [] };
    } catch (error) {
      console.error('Error fetching stock withdrawals:', error);
      throw readFailed('the withdrawal history', error);
    }
  },

  deleteStockWithdrawal: async (withdrawalId: number) => {
    if (!isSupabaseConfigured()) {
      return { success: true };
    }

    try {
      const { data: withdrawal, error: fetchError } = await (supabase as any)
        .from('stock_withdrawals')
        .select('*')
        .eq('withdrawal_id', withdrawalId)
        .single();

      if (fetchError || !withdrawal) throw new Error('Stock withdrawal record not found');

      const { item_id, distribution_id, stall_id, quantity_withdrawn } = withdrawal;

      // Prefer per-batch detail rows (multi-batch stall return → one history line).
      let parts: Array<{ distribution_id: number; quantity_withdrawn: number }> = [];
      try {
        parts = await fetchWithdrawalBatchParts([withdrawalId]);
      } catch {
        parts = [];
      }

      if (parts.length > 0) {
        for (const part of parts) {
          const { data: dist } = await (supabase as any)
            .from('stock_distribution')
            .select('distribution_id, quantity_allocated')
            .eq('distribution_id', part.distribution_id)
            .maybeSingle();

          if (dist) {
            const { error: restoreError } = await (supabase as any)
              .from('stock_distribution')
              .update({
                quantity_allocated:
                  (Number(dist.quantity_allocated) || 0) + (Number(part.quantity_withdrawn) || 0)
              })
              .eq('distribution_id', part.distribution_id);
            if (restoreError) throw restoreError;
          } else if (stall_id != null) {
            const { error: recreateError } = await (supabase as any)
              .from('stock_distribution')
              .insert([{
                item_id,
                stall_id,
                quantity_allocated: Number(part.quantity_withdrawn) || 0,
                distributed_by: withdrawal.withdrawn_by || 1,
                notes: 'Restored from deleted stall withdrawal record'
              }]);
            if (recreateError) throw recreateError;
          }
        }
      } else if (distribution_id != null || stall_id != null) {
        // Legacy single-batch stall return (distribution_id set on the withdrawal).
        if (distribution_id != null) {
          const { data: dist } = await (supabase as any)
            .from('stock_distribution')
            .select('distribution_id, quantity_allocated')
            .eq('distribution_id', distribution_id)
            .maybeSingle();

          if (dist) {
            const { error: restoreError } = await (supabase as any)
              .from('stock_distribution')
              .update({ quantity_allocated: dist.quantity_allocated + quantity_withdrawn })
              .eq('distribution_id', distribution_id);
            if (restoreError) throw restoreError;
          } else if (stall_id != null) {
            const { error: recreateError } = await (supabase as any)
              .from('stock_distribution')
              .insert([{
                item_id,
                stall_id,
                quantity_allocated: quantity_withdrawn,
                distributed_by: withdrawal.withdrawn_by || 1,
                notes: 'Restored from deleted stall withdrawal record'
              }]);
            if (recreateError) throw recreateError;
          }
        } else if (stall_id != null) {
          // Stall return with no linked batch and no parts — put stock back as a new batch.
          const { error: recreateError } = await (supabase as any)
            .from('stock_distribution')
            .insert([{
              item_id,
              stall_id,
              quantity_allocated: quantity_withdrawn,
              distributed_by: withdrawal.withdrawn_by || 1,
              notes: 'Restored from deleted stall withdrawal record'
            }]);
          if (recreateError) throw recreateError;
        }
      }

      const { error: deleteError } = await (supabase as any)
        .from('stock_withdrawals')
        .delete()
        .eq('withdrawal_id', withdrawalId);

      if (deleteError) throw deleteError;

      await recomputeItemTotals(item_id);

      return { success: true };
    } catch (error) {
      console.error('Error deleting stock withdrawal:', error);
      throw error;
    }
  },

  // Inventory
  getInventory: async (stallId?: number) => {
    if (!isSupabaseConfigured()) {
      console.warn('⚠️ Supabase not configured, using mock data for inventory');
      return mockApi.getInventory(stallId);
    }

    // Ensure stallId is a number if provided
    const numericStallId = stallId != null ? Number(stallId) : undefined;
    const isInvalidStallId = numericStallId != null && (isNaN(numericStallId) || numericStallId <= 0);

    if (isInvalidStallId) {
      console.error(`[Get Inventory] Invalid stallId provided:`, stallId);
      return { items: [] };
    }

    console.log(`[Get Inventory] Fetching inventory${numericStallId ? ` for stall ${numericStallId}` : ' (admin view)'}`);

    try {
      let query = (supabase as any)
        .from('items')
        .select('*');

      if (numericStallId) {
        // Get items distributed to this stall
        console.log(`[Get Inventory] Querying distributions for stall ${numericStallId}...`);
        const { data: distributions, error: distError } = await fetchAllRows(
          'stock_distribution',
          'item_id, quantity_allocated',
          'distribution_id',
          (q: any) => q.eq('stall_id', numericStallId)
        );

        if (distError) {
          console.error(`[Get Inventory] Error fetching distributions for stall ${numericStallId}:`, distError);
          // Don't throw, maybe fallback to offline or return empty
        }

        const itemIds = Array.from(
          new Set((distributions || []).map((d: any) => d.item_id).filter((id: any) => id != null))
        );
        console.log(`[Get Inventory] Stall ${numericStallId} - Found ${itemIds.length} distributions, item_ids:`, itemIds);

        if (itemIds.length > 0) {
          query = (query as any).in('item_id', itemIds);
          console.log(`[Get Inventory] Querying items from 'items' table where item_id IN:`, itemIds);
        } else {
          console.log(`[Get Inventory] No items distributed to stall ${numericStallId} (no records found in stock_distribution)`);
          return { items: [] };
        }
      }

      const { data, error } = await (query as any).order('date_added', { ascending: false });

      if (error) {
        console.error(`[Get Inventory] Error fetching items:`, error);
        throw error;
      }

      console.log(`[Get Inventory] Fetched ${data?.length || 0} items from database`);

      // Calculate current stock based on distributions and sales
      console.log(`[Get Inventory] Processing ${data?.length || 0} items, stallId: ${numericStallId}`);

      const itemRows: any[] = data || [];
      const itemIds = itemRows.map((item: any) => item.item_id).filter((id: any) => id != null);

      if (itemIds.length === 0) {
        return { items: [] };
      }

      // Load history for every item up front. Querying per item cost four round
      // trips per row (145 requests for 36 items) before the page could render;
      // this stays a fixed handful however large the catalogue grows.
      const isAdminView = numericStallId === undefined;
      const empty = Promise.resolve({ data: [] as any[], error: null });

      const [additionsRes, distributionsRes, salesRes, withdrawalsRes] = await Promise.all([
        isAdminView
          ? fetchAllRows(
              'stock_additions',
              'item_id, quantity_added, date_added, addition_id',
              'addition_id',
              (q: any) => q.in('item_id', itemIds)
            )
          : empty,
        fetchAllRows(
          'stock_distribution',
          'item_id, stall_id, quantity_allocated, date_distributed, distribution_id',
          'distribution_id',
          (q: any) => {
            const scoped = q.in('item_id', itemIds);
            return isAdminView ? scoped : scoped.eq('stall_id', numericStallId);
          }
        ),
        fetchAllRows(
          'sales',
          'item_id, stall_id, quantity_sold, date_time, sale_id',
          'sale_id',
          (q: any) => {
            const scoped = q.in('item_id', itemIds);
            return isAdminView ? scoped.is('stall_id', null) : scoped.eq('stall_id', numericStallId);
          }
        ),
        isAdminView
          ? fetchAllRows(
              'stock_withdrawals',
              'item_id, stall_id, distribution_id, quantity_withdrawn, date_withdrawn, withdrawal_id',
              'withdrawal_id',
              (q: any) => q.in('item_id', itemIds)
            )
          : empty
      ]);

      const historyError =
        additionsRes.error || distributionsRes.error || salesRes.error || withdrawalsRes.error;
      if (historyError) {
        // Stall stock is allocated − sold. Empty failed sales would look like
        // zero sold and invent availability. Fail closed for stall views.
        if (!isAdminView) {
          throw readFailed('stall inventory history', historyError);
        }
        console.warn('[Get Inventory] History query failed - falling back to stored totals:', historyError);
      }

      const additionsByItem = groupByItemId(additionsRes.data || []);
      const distributionsByItem = groupByItemId(distributionsRes.data || []);
      const salesByItem = groupByItemId(salesRes.data || []);
      const withdrawalsByItem = groupByItemId(withdrawalsRes.data || []);

      const batchPartsByItem = new Map<number, any[]>();
      if (isAdminView && !historyError) {
        const allWithdrawalIds = (withdrawalsRes.data || []).map((w: any) => Number(w.withdrawal_id));
        const withdrawalItemById = new Map<number, number>();
        for (const w of withdrawalsRes.data || []) {
          withdrawalItemById.set(Number(w.withdrawal_id), Number(w.item_id));
        }
        try {
          const allParts = await fetchWithdrawalBatchParts(allWithdrawalIds);
          for (const part of allParts) {
            const itemId = withdrawalItemById.get(Number(part.withdrawal_id));
            if (itemId == null) continue;
            const bucket = batchPartsByItem.get(itemId) || [];
            bucket.push(part);
            batchPartsByItem.set(itemId, bucket);
          }
        } catch (partsError) {
          console.warn('[Get Inventory] Could not load withdrawal batch parts:', partsError);
        }
      }

      const items = itemRows.map((item: any) => {
        if (numericStallId !== undefined) {
          console.log(`[Get Inventory] Processing item ${item.item_id} (${item.item_name}) for stall ${numericStallId}`);
          // For specific stall: calculate distributed - sold for that stall
          const distributions = distributionsByItem.get(item.item_id) || [];

          const sortedDistributions = (distributions || []).sort((a: any, b: any) =>
            new Date(a.date_distributed).getTime() - new Date(b.date_distributed).getTime()
          );

          const totalDistributed = sortedDistributions.reduce((sum: number, d: any) => sum + d.quantity_allocated, 0);

          // Get all sales for this item at this stall
          const sales = salesByItem.get(item.item_id) || [];

          const totalSold = (sales || []).reduce((sum: number, s: any) => sum + s.quantity_sold, 0) || 0;

          // Calculate initial_stock and total_added based on distribution history
          // Initial stock = stock the user had at the time of the most recent distribution
          // Total added = quantity from the most recent distribution
          let initialStock = 0;
          let totalAdded = 0;

          if (sortedDistributions.length > 0) {
            const mostRecentDistribution = sortedDistributions[sortedDistributions.length - 1];
            totalAdded = mostRecentDistribution.quantity_allocated;

            if (sortedDistributions.length === 1) {
              // First distribution: initial = 0 (no stock before first distribution)
              initialStock = 0;
            } else {
              // For subsequent distributions: calculate stock at the time of the most recent distribution
              // This is: all previous distributions minus all sales up to that point
              const previousDistributions = sortedDistributions.slice(0, -1);
              const totalPreviousDistributed = previousDistributions.reduce(
                (sum: number, d: any) => sum + d.quantity_allocated, 0
              );

              // Calculate all sales that happened strictly before the most recent distribution
              // We use < (not <=) to ensure we get the stock at the moment before the new distribution
              const lastDistributionDate = new Date(mostRecentDistribution.date_distributed);
              const salesBeforeDistribution = (sales || []).filter(
                (s: any) => new Date(s.date_time) < lastDistributionDate
              );
              const totalSalesBeforeDistribution = salesBeforeDistribution.reduce(
                (sum: number, s: any) => sum + s.quantity_sold, 0
              );

              // Initial stock = stock the user had at that moment (before the new distribution)
              // This represents their "present stock" at the time they received the new stock
              // This is: previous distributions - sales before that point
              initialStock = Math.max(0, totalPreviousDistributed - totalSalesBeforeDistribution);
            }
          }

          // Current stock = total distributed - total sold
          const currentStock = Math.max(0, totalDistributed - totalSold);

          console.log(`[User Stock Calc] Item: ${item.item_name}, Stall: ${stallId}, Distributions: ${sortedDistributions.length}, initialStock: ${initialStock}, totalAdded: ${totalAdded}, currentStock: ${currentStock}`);

          // IMPORTANT: Explicitly create new object with calculated values
          // DO NOT use spread operator on item - it might include the original initial_stock
          const userItem = {
            item_id: item.item_id,
            stall_id: numericStallId, // Explicitly include stall_id for offline storage filtering
            item_name: item.item_name,
            category: item.category,
            sku: item.sku,
            unit_price: item.unit_price,
            buying_price: item.buying_price,
            date_added: item.date_added,
            current_stock: currentStock,
            initial_stock: initialStock, // Use calculated value (0 for first distribution)
            total_added: totalAdded,
            total_allocated: totalDistributed // For compatibility
          };

          console.log(`[User Stock Calc] Returning item:`, {
            item_id: userItem.item_id,
            item_name: userItem.item_name,
            original_initial_stock: item.initial_stock,
            calculated_initial_stock: userItem.initial_stock,
            total_added: userItem.total_added,
            current_stock: userItem.current_stock,
            distributions_count: sortedDistributions.length
          });

          // CRITICAL: Verify the calculated value is actually in the returned object
          if (userItem.initial_stock !== initialStock) {
            console.error(`[ERROR] initial_stock mismatch! Calculated: ${initialStock}, Returned: ${userItem.initial_stock}`);
          }

          // Force set the value one more time to be absolutely sure
          userItem.initial_stock = initialStock;
          userItem.total_added = totalAdded;

          console.log(`[User Stock Calc] FINAL VERIFICATION - initial_stock: ${userItem.initial_stock}, total_added: ${userItem.total_added}`);

          return userItem;
        } else {
          // For admin view: calculate actual available stock (initial + additions - total sold)
          const initialStock = item.initial_stock != null ? Number(item.initial_stock) : 0;
          const existingCurrentStock = item.current_stock != null ? Number(item.current_stock) : 0;

          // CRITICAL: if ANY history query failed, use the stored item as-is.
          // A failed query reads as an empty list (sum 0); recomputing from
          // partial data would show garbage totals.
          if (historyError) {
            return item;
          }

          const additions = additionsByItem.get(item.item_id) || [];
          const distributions = distributionsByItem.get(item.item_id) || [];
          const centralSales = salesByItem.get(item.item_id) || [];
          const withdrawals = withdrawalsByItem.get(item.item_id) || [];
          const withdrawalBatchParts = batchPartsByItem.get(item.item_id) || [];

          const totalAdded = additions.reduce(
            (sum: number, a: any) => sum + (a.quantity_added || 0), 0);

          const totalDistributed = distributions.reduce(
            (sum: number, distribution: any) => sum + (distribution.quantity_allocated || 0), 0);

          const totalCentralSold = centralSales.reduce(
            (sum: number, sale: any) => sum + (sale.quantity_sold || 0), 0);

          const centralWithdrawn = withdrawals
            .filter((w: any) => w.stall_id == null)
            .reduce((sum: number, w: any) => sum + (w.quantity_withdrawn || 0), 0);
          const stallLedger = summarizeStallReturnLedger(distributions, withdrawals);
          const totalReceived = initialStock + totalAdded;

          const displayedStock = computeHubStock({
            initialStock,
            additions,
            distributions,
            withdrawals,
            centralSales,
            withdrawalBatchParts
          });

          // Do not write replay/current_stock back to items here. A DB trigger
          // or later poll would floor deficit items at 0 and the hub credit
          // would vanish after a few seconds.

          console.log(`[Admin Stock Calc] ${item.item_name} breakdown:
            Initial: ${initialStock}
            Additions: ${totalAdded}
            Total Received: ${totalReceived}
            ---
            Distributed: ${totalDistributed}
            Hub Sales: ${totalCentralSold}
            Central withdrawn: ${centralWithdrawn}
            Stall returned: ${stallLedger.stallReturned}
            Stall returns at hub (FIFO): ${stallLedger.netAtHub}
            ---
            Result Central Stock: ${displayedStock} (stored ${existingCurrentStock})`);

          return {
            ...item,
            current_stock: displayedStock,
            initial_stock: initialStock,
            total_added: totalAdded,
            total_allocated: totalDistributed
          };
        }
      });

      return { items };
    } catch (error) {
      console.error('Error fetching inventory:', error);
      throw readFailed('inventory', error);
    }
  },

  createItem: async (itemData: any) => {
    if (!isSupabaseConfigured()) {
      console.warn('⚠️ Supabase not configured, using mock data');
      return mockApi.createItem(itemData as any);
    }

    try {
      // Ensure initial_stock is a number
      const itemToInsert = {
        ...itemData,
        initial_stock: Number(itemData.initial_stock) || 0,
        current_stock: Number(itemData.current_stock) || Number(itemData.initial_stock) || 0
      };

      console.log('[Create Item] Inserting item:', itemToInsert);

      const { data, error } = await (supabase as any)
        .from('items')
        .insert([itemToInsert])
        .select()
        .single();

      if (error) {
        console.error('[Create Item] Supabase error:', error);
        throw error;
      }

      console.log('[Create Item] Item created successfully:', data);

      // Note: We don't create stock_additions for initial_stock
      // initial_stock is the starting stock, and stock_additions tracks additional stock added later
      // Admin Stock = initial_stock + total_added (from stock_additions) - total_distributed

      return { item: data as Item };
    } catch (error) {
      console.error('Error creating item:', error);
      throw error;
    }
  },

  updateItem: async (itemId: number, itemData: any) => {
    if (!isSupabaseConfigured()) {
      return mockApi.updateItem(itemId, itemData as any);
    }

    try {
      const numericItemId = typeof itemId === 'number' ? itemId : parseInt(itemId as any, 10);
      if (!Number.isFinite(numericItemId)) {
        throw new Error('Invalid item reference.');
      }

      const { data: existingItem, error: fetchError } = await (supabase as any)
        .from('items')
        .select('item_id, item_name, category, unit_price, initial_stock, total_added, current_stock, total_allocated')
        .eq('item_id', numericItemId)
        .single();

      if (fetchError || !existingItem) {
        throw new Error('Item not found.');
      }

      const updateData: Record<string, any> = {};

      if (itemData.item_name !== undefined) {
        updateData.item_name = String(itemData.item_name).trim();
      }

      if (itemData.category !== undefined) {
        updateData.category = String(itemData.category).trim();
      }

      if (itemData.unit_price !== undefined) {
        const unitPrice = Number(itemData.unit_price);
        if (!Number.isFinite(unitPrice) || unitPrice <= 0) {
          throw new Error('Selling price must be greater than zero.');
        }
        updateData.unit_price = unitPrice;
      }

      if (itemData.buying_price !== undefined) {
        const buyingPrice = Number(itemData.buying_price);
        if (!Number.isFinite(buyingPrice) || buyingPrice < 0) {
          throw new Error('Buying price must be zero or greater.');
        }
        updateData.buying_price = buyingPrice;
      }

      if (itemData.initial_stock !== undefined || itemData.total_added !== undefined) {
        if (itemData.initial_stock !== undefined) {
          const initialStock = Number(itemData.initial_stock);
          if (!Number.isFinite(initialStock) || initialStock < 0) {
            throw new Error('Initial stock must be zero or greater.');
          }
          updateData.initial_stock = initialStock;
        }

        const currentTotalAdded = Number(existingItem.total_added || 0);

        if (itemData.total_added !== undefined) {
          const newTotalAdded = Number(itemData.total_added);
          if (!Number.isFinite(newTotalAdded)) {
            throw new Error('Invalid total added value.');
          }

          const quantityToAdd = newTotalAdded - currentTotalAdded;
          if (quantityToAdd > 0) {
            const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
            const { error: insertError } = await (supabase as any)
              .from('stock_additions')
              .insert([{
                item_id: numericItemId,
                quantity_added: quantityToAdd,
                added_by: currentUser.user_id || 1
              }]);

            if (insertError) {
              console.error('Error creating stock_additions record:', insertError);
              throw new Error(insertError.message || 'Failed to record added stock.');
            }
          }
        }
      }

      if (Object.keys(updateData).length === 0) {
        return { item: existingItem as Item };
      }

      const { error: updateError } = await (supabase as any)
        .from('items')
        .update(updateData)
        .eq('item_id', numericItemId);

      if (updateError) throw updateError;

      const item = await recomputeItemTotals(numericItemId);
      return { item };
    } catch (error) {
      console.error('Error updating item:', error);
      throw new Error((error as any)?.message || 'Failed to update item.');
    }
  },

  // Add stock using the exact quantity the user typed (delta), never a
  // recomputed cumulative total. This prevents stale on-screen data from
  // shrinking/inflating additions when multiple people use the system.
  addStock: async (itemId: number, quantity: number) => {
    if (!isSupabaseConfigured()) {
      const inventory = await mockApi.getInventory();
      const mockItem = (inventory.items || []).find((i: any) => i.item_id === itemId);
      return mockApi.updateItem(itemId, {
        total_added: ((mockItem as any)?.total_added || 0) + quantity
      } as any);
    }

    const numericItemId = typeof itemId === 'number' ? itemId : parseInt(itemId as any, 10);
    const quantityToAdd = Number(quantity);

    if (!Number.isFinite(numericItemId)) {
      throw new Error('Invalid item reference.');
    }
    if (!Number.isInteger(quantityToAdd) || quantityToAdd <= 0) {
      throw new Error('Quantity to add must be a whole number greater than zero.');
    }

    const addedBy = getCurrentUserId();

    try {
      // Preferred path: atomic, row-locked DB function
      const { data, error } = await (supabase as any).rpc('add_stock_atomic', {
        p_item_id: numericItemId,
        p_quantity: quantityToAdd,
        p_added_by: addedBy
      });

      if (!error && data) {
        const item = (Array.isArray(data) ? data[0] : data) as Item;
        console.log('[Add Stock] Atomic RPC success:', { itemId: numericItemId, quantityToAdd, current_stock: (item as any).current_stock });
        return { item };
      }

      if (error && !isMissingFunctionError(error)) {
        throw new Error(friendlyDbError(error));
      }

      // Fallback (atomic function not installed yet): insert the history row
      // with the user's exact quantity, then recompute totals from history.
      console.warn('[Add Stock] add_stock_atomic not installed, using fallback path');

      const { data: hubRow } = await (supabase as any)
        .from('items')
        .select('current_stock')
        .eq('item_id', numericItemId)
        .maybeSingle();
      const hubBefore = Math.max(0, Number(hubRow?.current_stock) || 0);

      const { error: insertError } = await (supabase as any)
        .from('stock_additions')
        .insert([{
          item_id: numericItemId,
          quantity_added: quantityToAdd,
          added_by: addedBy,
          stock_before: hubBefore,
          stock_after: hubBefore + quantityToAdd
        }]);

      if (insertError) {
        throw new Error(insertError.message || 'Failed to record added stock.');
      }

      const item = await recomputeItemTotals(numericItemId);
      return { item };
    } catch (error) {
      console.error('Error adding stock:', error);
      throw new Error((error as any)?.message || 'Failed to add stock.');
    }
  },

  distributeStock: async (distributionData: {
    item_id: number;
    distributions: Array<{ stall_id: number; quantity: number }>;
    notes?: string;
  }): Promise<{ distributions: Array<{ item_id: number; stall_id: number; quantity_allocated: number; date_distributed: string }> }> => {
    if (!isSupabaseConfigured()) {
      // Call mockApi with the correct signature - it expects (itemId, distribution)
      // We'll call it for each distribution
      for (const dist of distributionData.distributions) {
        await (mockApi.distributeStock as any)(distributionData.item_id, {
          stall_id: dist.stall_id,
          quantity_allocated: dist.quantity
        });
      }
      return {
        distributions: distributionData.distributions.map(d => ({
          item_id: distributionData.item_id,
          stall_id: d.stall_id,
          quantity_allocated: d.quantity,
          date_distributed: new Date().toISOString()
        }))
      };
    }

    try {
      const distributedBy = getCurrentUserId();

      const validDistributions = distributionData.distributions
        .filter(dist => (dist.quantity || 0) > 0 && dist.stall_id);

      const totalToDistribute = validDistributions.reduce((sum, dist) => sum + (dist.quantity || 0), 0);
      if (totalToDistribute <= 0) {
        throw new Error('Distribution quantity must be greater than zero.');
      }

      // Preferred path: atomic, row-locked DB function (validates stock
      // against history inside one transaction).
      const { data: rpcData, error: rpcError } = await (supabase as any).rpc('distribute_stock_atomic_v2', {
        p_item_id: distributionData.item_id,
        p_distributions: validDistributions.map(d => ({ stall_id: d.stall_id, quantity: d.quantity })),
        p_distributed_by: distributedBy,
        p_notes: distributionData.notes || ''
      });

      if (!rpcError) {
        console.log('[Distribute Stock] Atomic RPC success:', rpcData);
        return { distributions: rpcData || [] };
      }

      if (!isMissingFunctionError(rpcError)) {
        throw new Error(friendlyDbError(rpcError));
      }

      // Function not installed yet. Still check the live hub figure here so
      // we never write an over-allocation while waiting for the migration.
      console.warn('[Distribute Stock] distribute_stock_atomic_v2 not installed, using fallback path');

      const { data: itemRow, error: itemErr } = await (supabase as any)
        .from('items')
        .select('initial_stock')
        .eq('item_id', distributionData.item_id)
        .single();
      if (itemErr || !itemRow) throw new Error('Item not found.');

      const [additionsRes, distributionsRes, centralSalesRes, withdrawalsRes] = await Promise.all([
        (supabase as any).from('stock_additions').select('quantity_added, date_added, addition_id').eq('item_id', distributionData.item_id),
        (supabase as any).from('stock_distribution').select('quantity_allocated, date_distributed, distribution_id, stall_id').eq('item_id', distributionData.item_id),
        (supabase as any).from('sales').select('quantity_sold, date_time, sale_id').eq('item_id', distributionData.item_id).is('stall_id', null),
        (supabase as any).from('stock_withdrawals').select('quantity_withdrawn, date_withdrawn, withdrawal_id, stall_id, distribution_id').eq('item_id', distributionData.item_id)
      ]);
      if (additionsRes.error || distributionsRes.error || centralSalesRes.error || withdrawalsRes.error) {
        throw new Error('Failed to load stock history for distribution.');
      }

      const available = computeHubStock({
        initialStock: itemRow.initial_stock || 0,
        additions: additionsRes.data || [],
        distributions: distributionsRes.data || [],
        withdrawals: withdrawalsRes.data || [],
        centralSales: centralSalesRes.data || [],
        withdrawalBatchParts: await fetchWithdrawalBatchParts(
          (withdrawalsRes.data || []).map((w: any) => Number(w.withdrawal_id))
        )
      });
      if (available < totalToDistribute) {
        throw new Error(`Only ${available} left at the central hub. You asked to send ${totalToDistribute}.`);
      }

      let hubRunning = available;
      const distributions = validDistributions.map(dist => {
        const before = hubRunning;
        const after = hubRunning - dist.quantity;
        hubRunning = after;
        return {
          item_id: distributionData.item_id,
          stall_id: dist.stall_id,
          quantity_allocated: dist.quantity,
          date_distributed: new Date().toISOString(),
          distributed_by: distributedBy,
          notes: distributionData.notes || '',
          stock_before: before,
          stock_after: after
        };
      });

      const { data, error } = await (supabase as any)
        .from('stock_distribution')
        .insert(distributions)
        .select();

      if (error) {
        console.error('[Distribute Stock] Supabase error:', error);
        throw error;
      }

      await recomputeItemTotals(distributionData.item_id);

      console.log('[Distribute Stock] Success:', data);
      return { distributions: data };
    } catch (error) {
      console.error('Error distributing stock:', error);
      throw error;
    }
  },

  getDistributions: async (itemId?: number) => {
    if (!isSupabaseConfigured()) {
      return (mockApi as any).getDistributions(itemId);
    }

    try {
      // Paged by primary key, then sorted for display, so the list stays
      // complete once this table grows past Supabase's 1000-row response cap.
      const { data, error } = await fetchAllRows(
        'stock_distribution',
        `
          *,
          stalls:stall_id(stall_name),
          users:distributed_by(full_name)
        `,
        'distribution_id',
        (query: any) => (itemId ? query.eq('item_id', itemId) : query)
      );

      if (error) throw error;

      const distributions = (data || [])
        .map((dist: any) => ({
          ...dist,
          stall_name: dist.stalls?.stall_name || 'Unknown',
          distributed_by_name: dist.users?.full_name || 'Unknown'
        }))
        .sort(
          (a: any, b: any) =>
            new Date(b.date_distributed).getTime() - new Date(a.date_distributed).getTime()
        );

      return { distributions };
    } catch (error) {
      console.error('Error fetching distributions:', error);
      throw error;
    }
  },

  updateDistribution: async (distributionId: number, quantity: number, stallId: number) => {
    if (!isSupabaseConfigured()) {
      return (mockApi as any).updateDistribution(distributionId, quantity, stallId);
    }

    try {
      // 1. Get the existing distribution to know the difference
      const { data: existingDist, error: fetchError } = await (supabase as any)
        .from('stock_distribution')
        .select('*')
        .eq('distribution_id', distributionId)
        .single();

      if (fetchError || !existingDist) throw new Error('Distribution not found');

      const itemId = existingDist.item_id;
      const oldStallId = Number(existingDist.stall_id);
      const oldQuantity = Number(existingDist.quantity_allocated) || 0;
      const quantityDiff = quantity - oldQuantity;
      const stallChanged = Number(stallId) !== oldStallId;

      // Sold units stay on the stall ledger. Shrinking or moving a batch must
      // leave enough other allocated stock to cover what's already sold there.
      const soldOnOldStall = await sumSoldAtStall(itemId, oldStallId);
      const { data: otherOnOld, error: otherOldErr } = await (supabase as any)
        .from('stock_distribution')
        .select('quantity_allocated')
        .eq('item_id', itemId)
        .eq('stall_id', oldStallId)
        .neq('distribution_id', distributionId);
      if (otherOldErr) throw otherOldErr;
      const otherAllocatedOld = (otherOnOld || []).reduce(
        (sum: number, row: any) => sum + (Number(row.quantity_allocated) || 0),
        0
      );
      const remainingOnOldAfter = stallChanged
        ? otherAllocatedOld
        : otherAllocatedOld + quantity;
      if (remainingOnOldAfter < soldOnOldStall) {
        throw new Error(
          `Cannot change this distribution: ${soldOnOldStall} unit(s) already sold at this stall. ` +
          `At least that many must stay allocated (would leave ${remainingOnOldAfter}).`
        );
      }

      if (stallChanged) {
        const soldOnNewStall = await sumSoldAtStall(itemId, Number(stallId));
        const { data: otherOnNew, error: otherNewErr } = await (supabase as any)
          .from('stock_distribution')
          .select('quantity_allocated')
          .eq('item_id', itemId)
          .eq('stall_id', stallId)
          .neq('distribution_id', distributionId);
        if (otherNewErr) throw otherNewErr;
        const otherAllocatedNew = (otherOnNew || []).reduce(
          (sum: number, row: any) => sum + (Number(row.quantity_allocated) || 0),
          0
        );
        if (otherAllocatedNew + quantity < soldOnNewStall) {
          throw new Error(
            `Cannot move this batch: destination stall already sold ${soldOnNewStall} unit(s) ` +
            `and would only have ${otherAllocatedNew + quantity} allocated.`
          );
        }
      }

      // Validate hub stock when increasing the batch size
      if (quantityDiff > 0) {
        const freshItem = await recomputeItemTotals(itemId);
        if (quantityDiff > Number((freshItem as any).current_stock || 0)) {
          throw new Error(
            `Insufficient stock! Available: ${(freshItem as any).current_stock}, Requested additional: ${quantityDiff}`
          );
        }
      }

      const { data, error: updateDistError } = await (supabase as any)
        .from('stock_distribution')
        .update({
          quantity_allocated: quantity,
          stall_id: stallId,
          date_distributed: new Date().toISOString()
        })
        .eq('distribution_id', distributionId)
        .select()
        .single();

      if (updateDistError) throw updateDistError;

      await recomputeItemTotals(itemId);

      return { distribution: data };
    } catch (error) {
      console.error('Error updating distribution:', error);
      throw error;
    }
  },

  deleteDistribution: async (distributionId: number) => {
    if (!isSupabaseConfigured()) {
      return (mockApi as any).deleteDistribution(distributionId);
    }

    try {
      // 1. Get the existing distribution
      const { data: existingDist, error: fetchError } = await (supabase as any)
        .from('stock_distribution')
        .select('*')
        .eq('distribution_id', distributionId)
        .single();

      if (fetchError || !existingDist) throw new Error('Distribution not found');

      const itemId = existingDist.item_id;

      // Refuse if stock was returned from this batch. The database would reject
      // the delete anyway with a raw foreign key error, and forcing it through
      // would credit the hub twice: once for the allocation disappearing and
      // again for the return that is still on record.
      const { data: linkedWithdrawals, error: linkedError } = await (supabase as any)
        .from('stock_withdrawals')
        .select('withdrawal_id, quantity_withdrawn')
        .eq('distribution_id', distributionId);

      if (linkedError) throw linkedError;

      if (linkedWithdrawals && linkedWithdrawals.length > 0) {
        const units = linkedWithdrawals.reduce(
          (sum: number, w: any) => sum + (Number(w.quantity_withdrawn) || 0), 0
        );
        throw new Error(
          `This distribution can't be deleted because ${units} unit(s) were already returned from it to the central hub. ` +
          `Delete those ${linkedWithdrawals.length} withdrawal record(s) first — that puts the stock back on this batch — then delete the distribution.`
        );
      }

      // Deleting the row credits the hub for the original allocation. If units
      // were sold at this stall, other batches must still cover those sales —
      // otherwise the delete invents hub stock that was already sold.
      const stallId = Number(existingDist.stall_id);
      const soldAtStall = await sumSoldAtStall(itemId, stallId);
      if (soldAtStall > 0) {
        const { data: otherBatches, error: otherErr } = await (supabase as any)
          .from('stock_distribution')
          .select('quantity_allocated')
          .eq('item_id', itemId)
          .eq('stall_id', stallId)
          .neq('distribution_id', distributionId);
        if (otherErr) throw otherErr;
        const otherAllocated = (otherBatches || []).reduce(
          (sum: number, row: any) => sum + (Number(row.quantity_allocated) || 0),
          0
        );
        if (otherAllocated < soldAtStall) {
          throw new Error(
            `Cannot delete this distribution: ${soldAtStall} unit(s) already sold at this stall. ` +
            `Other batches only cover ${otherAllocated}. Withdraw unsold units instead, or leave enough allocated to cover sales.`
          );
        }
      }

      // 2. Delete the distribution
      const { error: deleteError } = await (supabase as any)
        .from('stock_distribution')
        .delete()
        .eq('distribution_id', distributionId);

      if (deleteError) throw deleteError;

      // 3. Recompute item totals from history (returns stock to central hub)
      await recomputeItemTotals(itemId);

      return { success: true };
    } catch (error) {
      console.error('Error deleting distribution:', error);
      throw error;
    }
  },

  // Withdraw partial or full quantity from a user's distribution
  withdrawFromDistribution: async (distributionId: number, quantityToWithdraw: number) => {
    if (!isSupabaseConfigured()) {
      return (mockApi as any).withdrawFromDistribution(distributionId, quantityToWithdraw);
    }

    try {
      const withdrawnBy = getCurrentUserId();

      const { data: rpcData, error: rpcError } = await (supabase as any).rpc(
        'withdraw_from_distribution_atomic',
        {
          p_distribution_id: distributionId,
          p_quantity: quantityToWithdraw,
          p_withdrawn_by: withdrawnBy
        }
      );

      if (!rpcError && rpcData) {
        const row = Array.isArray(rpcData) ? rpcData[0] : rpcData;
        return {
          success: true,
          withdrawnQuantity: Number(row.withdrawnQuantity) || quantityToWithdraw,
          withdrawalId: row.withdrawalId ?? null,
          stallName: row.stallName ?? null,
          itemId: row.itemId,
          newCentralStock: Number(row.newCentralStock) || 0
        };
      }

      if (rpcError && !isMissingFunctionError(rpcError)) {
        throw new Error(friendlyDbError(rpcError));
      }

      const { data: batch, error: batchError } = await (supabase as any)
        .from('stock_distribution')
        .select('distribution_id, item_id, stall_id, quantity_allocated, stalls:stall_id(stall_name)')
        .eq('distribution_id', distributionId)
        .maybeSingle();

      if (batchError) throw batchError;
      if (!batch) throw new Error('Distribution batch not found. Please refresh and try again.');

      const allocated = Number(batch.quantity_allocated) || 0;
      if (quantityToWithdraw <= 0) throw new Error('Quantity must be greater than 0.');

      // Sales do not shrink quantity_allocated — only unsold units can return to hub.
      const soldAtStall = await sumSoldAtStall(Number(batch.item_id), Number(batch.stall_id));
      const { data: stallBatches, error: stallBatchesError } = await (supabase as any)
        .from('stock_distribution')
        .select('quantity_allocated')
        .eq('item_id', batch.item_id)
        .eq('stall_id', batch.stall_id);
      if (stallBatchesError) throw stallBatchesError;
      const totalAllocated = (stallBatches || []).reduce(
        (sum: number, row: any) => sum + (Number(row.quantity_allocated) || 0),
        0
      );
      const stallLeft = Math.max(0, totalAllocated - soldAtStall);
      const maxReturnable = Math.min(allocated, stallLeft);

      if (quantityToWithdraw > maxReturnable) {
        throw new Error(
          `Only ${maxReturnable} unit(s) left to return from this stall ` +
          `(batch has ${allocated}, unsold at stall ${stallLeft}).`
        );
      }

      const remaining = allocated - quantityToWithdraw;

      // Shrink the batch rather than deleting it when it empties. A withdrawal
      // record points back at its batch, so deleting the row is rejected by the
      // foreign key, and the ledger needs the row to rebuild how much originally
      // left the hub. Matching on the quantity we just read also means two
      // people withdrawing at once cannot both spend the same units - the
      // second update matches no row and is rejected.
      const { data: updatedRows, error: updateError } = await (supabase as any)
        .from('stock_distribution')
        .update({ quantity_allocated: remaining })
        .eq('distribution_id', distributionId)
        .eq('quantity_allocated', allocated)
        .select('distribution_id');

      if (updateError) throw updateError;
      if (!updatedRows || updatedRows.length === 0) {
        throw new Error('This stock was just changed by someone else. Please refresh and try again.');
      }

      const stallBefore = stallLeft;
      const stallAfter = Math.max(0, stallLeft - quantityToWithdraw);

      const { data: withdrawal, error: insertError } = await (supabase as any)
        .from('stock_withdrawals')
        .insert([{
          item_id: batch.item_id,
          stall_id: batch.stall_id,
          distribution_id: distributionId,
          quantity_withdrawn: quantityToWithdraw,
          reason: 'Returned to central hub',
          notes: `↩️ Returned ${quantityToWithdraw} units from stall to central hub.`,
          withdrawn_by: withdrawnBy,
          stock_before: stallBefore,
          stock_after: stallAfter
        }])
        .select('withdrawal_id')
        .single();

      if (insertError) {
        // Put the units back so a failed audit write cannot silently lose stock.
        await (supabase as any)
          .from('stock_distribution')
          .update({ quantity_allocated: allocated })
          .eq('distribution_id', distributionId);
        throw insertError;
      }

      const item = await recomputeItemTotals(batch.item_id);

      return {
        success: true,
        withdrawnQuantity: quantityToWithdraw,
        withdrawalId: withdrawal?.withdrawal_id ?? null,
        stallName: (batch as any).stalls?.stall_name ?? null,
        itemId: batch.item_id,
        newCentralStock: Number((item as any)?.current_stock) || 0
      };
    } catch (error) {
      console.error('[WFD] Error:', error);
      throw error;
    }
  },

  /**
   * Stall → central return for a quantity that may span multiple distribution
   * batches. Drains batches oldest-first and writes ONE withdrawal history row
   * (plus per-batch detail rows for the hub ledger).
   */
  withdrawFromStall: async (params: {
    item_id: number;
    stall_id: number;
    quantity: number;
    reason?: string;
    notes?: string;
  }) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Stall withdrawal requires Supabase.');
    }

    const quantity = Number(params.quantity);
    if (!Number.isInteger(quantity) || quantity <= 0) {
      throw new Error('Withdrawal quantity must be a whole number greater than zero.');
    }

    try {
      const itemId = params.item_id;
      const stallId = params.stall_id;
      const withdrawnBy = getCurrentUserId();

      const { data: rpcData, error: rpcError } = await (supabase as any).rpc(
        'withdraw_from_stall_atomic',
        {
          p_item_id: itemId,
          p_stall_id: stallId,
          p_quantity: quantity,
          p_withdrawn_by: withdrawnBy,
          p_reason: params.reason || 'Returned to central hub',
          p_notes: params.notes || 'Moved from stall back to central hub'
        }
      );

      if (!rpcError && rpcData) {
        const row = Array.isArray(rpcData) ? rpcData[0] : rpcData;
        return {
          success: true,
          withdrawnQuantity: Number(row.withdrawnQuantity) || quantity,
          withdrawalId: row.withdrawalId ?? null,
          stallName: row.stallName ?? `Stall #${stallId}`,
          itemId,
          newCentralStock: Number(row.newCentralStock) || 0
        };
      }

      if (rpcError && !isMissingFunctionError(rpcError)) {
        throw new Error(friendlyDbError(rpcError));
      }

      // Fallback when the atomic RPC is not installed: drain batches, then
      // write ONE withdrawal + detail rows (same shape as the RPC).
      const { data: batches, error: batchError } = await (supabase as any)
        .from('stock_distribution')
        .select('distribution_id, quantity_allocated, date_distributed, stalls:stall_id(stall_name)')
        .eq('item_id', itemId)
        .eq('stall_id', stallId)
        .order('date_distributed', { ascending: true });

      if (batchError) throw batchError;

      const sorted = (batches || []).sort(
        (a: any, b: any) =>
          new Date(a.date_distributed).getTime() - new Date(b.date_distributed).getTime()
      );
      const available = Math.max(
        0,
        sorted.reduce(
          (sum: number, b: any) => sum + (Number(b.quantity_allocated) || 0),
          0
        ) - (await sumSoldAtStall(itemId, stallId))
      );
      if (available < quantity) {
        throw new Error(`Insufficient stock at stall. Available (allocated − sold): ${available}`);
      }

      let remaining = quantity;
      const parts: Array<{ distribution_id: number; quantity_withdrawn: number }> = [];
      let stallName = `Stall #${stallId}`;

      for (const batch of sorted) {
        if (remaining <= 0) break;
        const batchQty = Number(batch.quantity_allocated) || 0;
        if (batchQty <= 0) continue;
        const take = Math.min(remaining, batchQty);

        const { data: updatedRows, error: updateError } = await (supabase as any)
          .from('stock_distribution')
          .update({ quantity_allocated: batchQty - take })
          .eq('distribution_id', batch.distribution_id)
          .eq('quantity_allocated', batchQty)
          .select('distribution_id');

        if (updateError) throw updateError;
        if (!updatedRows || updatedRows.length === 0) {
          throw new Error('This stock was just changed by someone else. Please refresh and try again.');
        }

        parts.push({ distribution_id: batch.distribution_id, quantity_withdrawn: take });
        if (batch.stalls?.stall_name) stallName = batch.stalls.stall_name;
        remaining -= take;
      }

      if (remaining > 0) {
        throw new Error(`Insufficient stock at stall. Could only withdraw ${quantity - remaining}.`);
      }

      const { data: withdrawal, error: insertError } = await (supabase as any)
        .from('stock_withdrawals')
        .insert([{
          item_id: itemId,
          stall_id: stallId,
          distribution_id: null,
          quantity_withdrawn: quantity,
          reason: params.reason || 'Returned to central hub',
          notes: params.notes || 'Moved from stall back to central hub',
          withdrawn_by: withdrawnBy,
          stock_before: available,
          stock_after: Math.max(0, available - quantity)
        }])
        .select('withdrawal_id')
        .single();

      if (insertError) throw insertError;

      const withdrawalId = withdrawal?.withdrawal_id;
      if (withdrawalId != null && parts.length > 0) {
        const { error: partsError } = await (supabase as any)
          .from('stock_withdrawal_batches')
          .insert(
            parts.map((p) => ({
              withdrawal_id: withdrawalId,
              distribution_id: p.distribution_id,
              quantity_withdrawn: p.quantity_withdrawn
            }))
          );
        if (partsError) {
          // Undo the parent row so we do not leave an unlinked return that
          // would mis-attribute original allocations on the earliest batch.
          await (supabase as any).from('stock_withdrawals').delete().eq('withdrawal_id', withdrawalId);
          throw partsError;
        }
      }

      const recomputed = await recomputeItemTotals(itemId);

      return {
        success: true,
        withdrawnQuantity: quantity,
        withdrawalId: withdrawalId ?? null,
        stallName,
        itemId,
        newCentralStock: Number((recomputed as any)?.current_stock) || 0
      };
    } catch (error) {
      console.error('[WithdrawFromStall] Error:', error);
      throw error;
    }
  },


  // Sales aggregates (all rows — avoids Supabase 1000-row default limit on inventory totals)
  getSalesAggregates: async (): Promise<SalesAggregates> => {
    if (!isSupabaseConfigured()) {
      return mockApi.getSalesAggregates();
    }

    try {
      const rows = await fetchAllSalesQuantityRows();
      return buildSalesAggregates(rows);
    } catch (error) {
      console.error('Error fetching sales aggregates:', error);
      throw readFailed('sales totals', error);
    }
  },

  // Sales
  getSales: async () => {
    if (!isSupabaseConfigured()) {
      return mockApi.getSales();
    }

    try {
      const allRows: any[] = [];
      let from = 0;

      while (true) {
        const { data, error } = await (supabase as any)
          .from('sales')
          .select(`
            *,
            users:recorded_by(full_name),
            stalls:stall_id(stall_name),
            items:item_id(item_name, category),
            credit_sales:credit_sales(sale_id, customer_name, customer_contact, payment_status, balance_due, amount_paid, due_date, notes)
          `)
          .order('date_time', { ascending: false })
          .range(from, from + SALES_PAGE_SIZE - 1);

        if (error) throw error;

        const page = data || [];
        allRows.push(...page);
        if (page.length < SALES_PAGE_SIZE) break;
        from += SALES_PAGE_SIZE;
      }

      const sales = allRows.map(mapSaleRow);

      return { sales: sales as Sale[] };
    } catch (error) {
      console.error('Error fetching sales:', error);
      throw readFailed('sales', error);
    }
  },

  createSale: async (saleData: SaleInput) => {
    if (!isSupabaseConfigured()) {
      return mockApi.createSale(saleData);
    }

    try {
      if (
        saleData.quantity_sold == null ||
        !Number.isInteger(Number(saleData.quantity_sold)) ||
        Number(saleData.quantity_sold) <= 0
      ) {
        throw new Error('Quantity sold must be a whole number greater than zero.');
      }

      if (saleData.unit_price === undefined || saleData.unit_price === null || saleData.unit_price < 0) {
        throw new Error('Selling price must be zero or greater.');
      }

      // Calculate total_amount if not provided
      const totalAmount = saleData.total_amount || (saleData.quantity_sold * saleData.unit_price);
      const itemId = typeof saleData.item_id === 'number' ? saleData.item_id : parseInt(saleData.item_id.toString(), 10);
      const stallId = saleData.stall_id === null || saleData.stall_id === undefined
        ? null
        : (typeof saleData.stall_id === 'number' ? saleData.stall_id : parseInt(saleData.stall_id.toString(), 10));

      if (saleData.sale_type === 'split') {
        const cashAmount = saleData.cash_amount ?? 0;
        const mobileAmount = saleData.mobile_amount ?? 0;

        if (cashAmount <= 0 || mobileAmount <= 0) {
          throw new Error('Split payment amounts must be greater than zero.');
        }

        if (Math.abs((cashAmount + mobileAmount) - totalAmount) > 0.5) {
          throw new Error('Split payment totals must equal the negotiated total.');
        }
      }

      const { data: rpcSale, error: rpcError } = await (supabase as any).rpc('create_sale_atomic', {
        p_item_id: itemId,
        p_stall_id: stallId,
        p_quantity_sold: saleData.quantity_sold,
        p_unit_price: saleData.unit_price,
        p_total_amount: totalAmount,
        p_sale_type: saleData.sale_type,
        p_recorded_by: saleData.recorded_by,
        p_cash_amount: saleData.sale_type === 'split' ? (saleData.cash_amount ?? null) : null,
        p_mobile_amount: saleData.sale_type === 'split' ? (saleData.mobile_amount ?? null) : null,
        p_customer_name: saleData.customer_name || null,
        p_customer_contact: saleData.customer_contact || null,
        p_amount_paid: saleData.amount_paid ?? null,
        p_due_date: saleData.due_date || null,
        p_notes: saleData.notes || null
      });

      if (!rpcError && rpcSale) {
        const sale = Array.isArray(rpcSale) ? rpcSale[0] : rpcSale;
        return { sale: sale as Sale };
      }

      if (rpcError && !isMissingFunctionError(rpcError)) {
        throw new Error(friendlyDbError(rpcError));
      }

      const saleToInsert = {
        item_id: itemId,
        stall_id: stallId,
        quantity_sold: saleData.quantity_sold,
        unit_price: saleData.unit_price,
        total_amount: totalAmount,
        sale_type: saleData.sale_type,
        cash_amount: saleData.sale_type === 'split' ? (saleData.cash_amount ?? null) : null,
        mobile_amount: saleData.sale_type === 'split' ? (saleData.mobile_amount ?? null) : null,
        recorded_by: saleData.recorded_by
      };

      const { data, error } = await (supabase as any)
        .from('sales')
        .insert([saleToInsert])
        .select()
        .single();

      if (error) throw new Error(friendlyDbError(error));

      if (saleData.sale_type === 'credit') {
        const amountPaid = saleData.amount_paid ?? 0;
        const paymentStatus = saleData.payment_status
          ?? (amountPaid >= totalAmount ? 'fully_paid' : amountPaid > 0 ? 'partially_paid' : 'unpaid');

        const { error: creditError } = await (supabase as any)
          .from('credit_sales')
          .insert([{
            sale_id: data.sale_id,
            customer_name: saleData.customer_name || 'Customer',
            customer_contact: saleData.customer_contact || 'N/A',
            total_credit_amount: totalAmount,
            amount_paid: amountPaid,
            payment_status: paymentStatus,
            due_date: saleData.due_date || null,
            notes: saleData.notes || null
          }]);

        if (creditError) {
          // The sale row is already in. Leaving it would create a credit sale
          // with no customer or balance attached to it, which then never shows
          // up for collection. Undo the sale so the operator can simply retry.
          await (supabase as any).from('sales').delete().eq('sale_id', data.sale_id);
          throw new Error(
            `Couldn't save the customer's credit details, so the sale was not recorded. Please enter it again. (${creditError.message || creditError})`
          );
        }
      }

      // Central hub sales reduce "Available to distribute" immediately.
      // Stall sales come out of distributed stock and don't touch current_stock.
      if (stallId === null) {
        try {
          await recomputeItemTotals(itemId);
        } catch (syncError) {
          console.warn('[Create Sale] Failed to sync central stock (will self-heal on next fetch):', syncError);
        }
      }

      return { sale: data as Sale };
    } catch (error) {
      console.error('Error creating sale:', error);
      throw error;
    }
  },

  updateSale: async (saleId: number, saleData: any) => {
    if (!isSupabaseConfigured()) {
      // Mock implementation
      return { sale: { ...saleData, sale_id: saleId } as Sale };
    }

    try {
      const { data: existingSale, error: existingError } = await (supabase as any)
        .from('sales')
        .select('*')
        .eq('sale_id', saleId)
        .single();

      if (existingError) throw existingError;

      const quantity = saleData.quantity_sold !== undefined ? saleData.quantity_sold : existingSale.quantity_sold;
      const unitPrice = saleData.unit_price !== undefined ? saleData.unit_price : existingSale.unit_price;
      const saleType = saleData.sale_type || existingSale.sale_type;

      if (!quantity || quantity <= 0) {
        throw new Error('Quantity must be greater than zero.');
      }

      if (!unitPrice || unitPrice <= 0) {
        throw new Error('Selling price must be greater than zero.');
      }

      const totalAmount = saleData.total_amount !== undefined
        ? saleData.total_amount
        : quantity * unitPrice;

      const stallId = saleData.stall_id === undefined
        ? existingSale.stall_id
        : (saleData.stall_id === null
          ? null
          : (typeof saleData.stall_id === 'number' ? saleData.stall_id : parseInt(saleData.stall_id.toString(), 10)));

      const itemId = saleData.item_id === undefined
        ? existingSale.item_id
        : (typeof saleData.item_id === 'number' ? saleData.item_id : parseInt(saleData.item_id.toString(), 10));

      if (saleType === 'split') {
        const cashAmount = saleData.cash_amount ?? existingSale.cash_amount ?? 0;
        const mobileAmount = saleData.mobile_amount ?? existingSale.mobile_amount ?? 0;

        if (cashAmount <= 0 || mobileAmount <= 0) {
          throw new Error('Split payment amounts must be greater than zero.');
        }

        if (Math.abs((cashAmount + mobileAmount) - totalAmount) > 0.5) {
          throw new Error('Split payment totals must equal the negotiated total.');
        }
      }

      const updatePayload: any = {
        item_id: itemId,
        stall_id: stallId,
        quantity_sold: quantity,
        unit_price: unitPrice,
        total_amount: totalAmount,
        sale_type: saleType,
        cash_amount: saleType === 'split' ? (saleData.cash_amount ?? existingSale.cash_amount ?? null) : null,
        mobile_amount: saleType === 'split' ? (saleData.mobile_amount ?? existingSale.mobile_amount ?? null) : null,
        recorded_by: saleData.recorded_by ?? existingSale.recorded_by
      };

      const { data, error } = await (supabase as any)
        .from('sales')
        .update(updatePayload)
        .eq('sale_id', saleId)
        .select()
        .single();

      if (error) throw new Error(friendlyDbError(error));

      const { data: existingCreditRows, error: existingCreditError } = await (supabase as any)
        .from('credit_sales')
        .select('*')
        .eq('sale_id', saleId)
        .limit(1);

      if (existingCreditError) throw existingCreditError;

      const existingCredit = Array.isArray(existingCreditRows) ? existingCreditRows[0] : null;

      if (saleType === 'credit') {
        const amountPaid = Number(saleData.amount_paid ?? existingCredit?.amount_paid ?? 0);

        if (!Number.isFinite(amountPaid) || amountPaid < 0) {
          throw new Error('Amount paid must be zero or greater.');
        }

        // balance_due is computed as total - paid, so a total below what the
        // customer already handed over would leave the shop showing a negative
        // amount owing.
        if (amountPaid > totalAmount) {
          throw new Error(
            `This customer has already paid ${amountPaid.toLocaleString()}, which is more than the new total of ${totalAmount.toLocaleString()}. ` +
            `Lower the amount paid first, or refund the difference, before reducing the sale.`
          );
        }

        // Status must follow the money, otherwise a sale can read "fully paid"
        // while a balance is still outstanding.
        const paymentStatus = amountPaid >= totalAmount
          ? 'fully_paid'
          : amountPaid > 0 ? 'partially_paid' : 'unpaid';

        const creditPayload: any = {
          customer_name: saleData.customer_name ?? existingCredit?.customer_name ?? 'Customer',
          customer_contact: saleData.customer_contact ?? existingCredit?.customer_contact ?? 'N/A',
          total_credit_amount: totalAmount,
          amount_paid: amountPaid,
          payment_status: paymentStatus,
          due_date: saleData.due_date ?? existingCredit?.due_date ?? null,
          notes: saleData.notes ?? existingCredit?.notes ?? null
        };

        // These writes carry the customer's balance. Swallowing an error here
        // let the sale change while the amount owing stayed as it was.
        const { error: creditWriteError } = existingCredit
          ? await (supabase as any)
              .from('credit_sales')
              .update(creditPayload)
              .eq('sale_id', saleId)
          : await (supabase as any)
              .from('credit_sales')
              .insert([{ sale_id: saleId, ...creditPayload }]);

        if (creditWriteError) {
          throw new Error(
            `The sale was updated but the customer's credit details could not be saved. Please reopen it and check the balance. (${creditWriteError.message || creditWriteError})`
          );
        }
      } else if (existingCredit) {
        const { error: creditRemoveError } = await (supabase as any)
          .from('credit_sales')
          .delete()
          .eq('sale_id', saleId);

        if (creditRemoveError) {
          throw new Error(
            `The sale was changed to ${saleType} but its old credit record could not be cleared, so it may still show as owing. (${creditRemoveError.message || creditRemoveError})`
          );
        }
      }

      // Re-sync hub stock for every item this edit touched. Changing item_id on
      // a hub sale must restore the old item as well as recompute the new one.
      try {
        const affectedItemIds = new Set<number>();
        if (existingSale.stall_id === null) affectedItemIds.add(Number(existingSale.item_id));
        if (stallId === null) affectedItemIds.add(Number(itemId));
        if (Number(existingSale.item_id) !== Number(itemId)) {
          if (existingSale.stall_id === null) affectedItemIds.add(Number(existingSale.item_id));
          if (stallId === null) affectedItemIds.add(Number(itemId));
        }
        for (const affectedId of Array.from(affectedItemIds)) {
          await recomputeItemTotals(affectedId);
        }
      } catch (syncError) {
        console.warn('[Update Sale] Failed to sync central stock (will self-heal on next fetch):', syncError);
      }

      return { sale: data as Sale };
    } catch (error) {
      console.error('Error updating sale:', error);
      throw error;
    }
  },

  deleteSale: async (saleId: number) => {
    if (!isSupabaseConfigured()) {
      return (mockApi as any).deleteSale(saleId);
    }

    try {
      console.log(`[Delete Sale] Deleting sale ID: ${saleId}`);

      // Look up the sale first so we know whether central stock must be restored
      const { data: saleRow } = await (supabase as any)
        .from('sales')
        .select('item_id, stall_id')
        .eq('sale_id', saleId)
        .single();

      // credit_sales cascades from sales. Clearing it by hand first meant a
      // failure on the sales delete left a credit sale with no customer or
      // balance, so it vanished from collections while still counting as a sale.
      const { error } = await (supabase as any)
        .from('sales')
        .delete()
        .eq('sale_id', saleId);

      if (error) throw error;

      // Deleting a central-hub sale puts the quantity back into "Available to distribute"
      if (saleRow && saleRow.stall_id === null) {
        try {
          await recomputeItemTotals(saleRow.item_id);
        } catch (syncError) {
          console.warn('[Delete Sale] Failed to sync central stock (will self-heal on next fetch):', syncError);
        }
      }

      console.log(`[Delete Sale] Successfully deleted sale ID: ${saleId}`);
      return { success: true };
    } catch (error) {
      console.error('Error deleting sale:', error);
      throw error;
    }
  },

  bulkDeleteSales: async (saleIds: number[]) => {
    if (!isSupabaseConfigured()) {
      for (const id of saleIds) {
        await (mockApi as any).deleteSale(id);
      }
      return { success: true };
    }

    try {
      console.log(`[Bulk Delete Sales] Deleting sale IDs:`, saleIds);

      // Track which items had central-hub sales so their stock can be restored.
      // Paged, because a single response stops at 1000 rows and any item past
      // that point would silently keep its stock unrestored.
      const { data: saleRows, error: lookupError } = await fetchAllRows(
        'sales',
        'sale_id, item_id, stall_id',
        'sale_id',
        (q: any) => q.in('sale_id', saleIds)
      );
      if (lookupError) throw lookupError;

      // credit_sales cascades from sales; deleting it separately risked leaving
      // credit sales stripped of their balances if the sales delete then failed.
      const { error } = await (supabase as any)
        .from('sales')
        .delete()
        .in('sale_id', saleIds);

      if (error) throw error;

      const centralItemIds = Array.from(new Set(
        (saleRows || [])
          .filter((s: any) => s.stall_id === null)
          .map((s: any) => Number(s.item_id))
      ));
      for (const affectedId of centralItemIds) {
        try {
          await recomputeItemTotals(affectedId as number);
        } catch (syncError) {
          console.warn('[Bulk Delete Sales] Failed to sync central stock for item', affectedId, syncError);
        }
      }

      console.log(`[Bulk Delete Sales] Successfully deleted ${saleIds.length} sales`);
      return { success: true };
    } catch (error) {
      console.error('Error bulk deleting sales:', error);
      throw error;
    }
  },

  createWithdrawal: async (withdrawalData: {
    item_id: number;
    quantity_withdrawn: number;
    reason: string;
    withdrawn_by: number;
    notes?: string;
    stall_id?: number | null;
    distribution_id?: number | null;
  }) => {
    if (!isSupabaseConfigured()) {
      return (mockApi as any).createWithdrawal(withdrawalData);
    }

    try {
      const quantity = Number(withdrawalData.quantity_withdrawn);
      if (!Number.isInteger(quantity) || quantity <= 0) {
        throw new Error('Withdrawal quantity must be a whole number greater than zero.');
      }

      const withdrawnBy = withdrawalData.withdrawn_by || getCurrentUserId();

      if (withdrawalData.stall_id != null || withdrawalData.distribution_id != null) {
        if (!withdrawalData.distribution_id) {
          throw new Error('Returning stock from a stall needs the distribution batch it came from.');
        }
        return dbApi.withdrawFromDistribution(withdrawalData.distribution_id, quantity);
      }

      // Preferred path: atomic, row-locked DB function
      const { data: rpcData, error: rpcError } = await (supabase as any).rpc('withdraw_stock_atomic', {
        p_item_id: withdrawalData.item_id,
        p_quantity: quantity,
        p_reason: withdrawalData.reason || 'General withdrawal',
        p_withdrawn_by: withdrawnBy,
        p_notes: withdrawalData.notes || null
      });

      if (!rpcError) {
        console.log('[Create Withdrawal] Atomic RPC success:', rpcData);
        // RPC returns an array; the first row contains the withdrawal_id
        const rpcRow = Array.isArray(rpcData) ? rpcData[0] : rpcData;
        return {
          withdrawal: {
            withdrawal_id: rpcRow?.withdrawal_id ?? null,
            item_id: withdrawalData.item_id,
            quantity_withdrawn: quantity,
            reason: withdrawalData.reason,
            withdrawn_by: withdrawnBy,
            notes: withdrawalData.notes || null,
            date_withdrawn: new Date().toISOString()
          }
        };
      }

      if (!isMissingFunctionError(rpcError)) {
        console.error('[Create Withdrawal] Atomic RPC error:', rpcError);
        throw new Error(friendlyDbError(rpcError));
      }

      // Fallback (atomic function not installed yet): validate against
      // freshly recomputed stock, insert the history row, recompute totals.
      console.warn('[Create Withdrawal] withdraw_stock_atomic not installed, using fallback path');

      const { data: hubRow } = await (supabase as any)
        .from('items')
        .select('current_stock')
        .eq('item_id', withdrawalData.item_id)
        .maybeSingle();
      const hubBefore = Math.max(0, Number(hubRow?.current_stock) || 0);

      const { data: inserted, error: insertError } = await (supabase as any)
        .from('stock_withdrawals')
        .insert([{
          item_id: withdrawalData.item_id,
          quantity_withdrawn: quantity,
          reason: withdrawalData.reason || 'General withdrawal',
          withdrawn_by: withdrawnBy,
          notes: withdrawalData.notes || null,
          stall_id: withdrawalData.stall_id ?? null,
          distribution_id: withdrawalData.distribution_id ?? null,
          stock_before: hubBefore,
          stock_after: Math.max(0, hubBefore - quantity)
        }])
        .select()
        .single();

      if (insertError) {
        throw new Error(insertError.message || 'Failed to create withdrawal');
      }

      await recomputeItemTotals(withdrawalData.item_id);

      return { withdrawal: inserted };
    } catch (error) {
      console.error('Error creating withdrawal:', error);
      throw error;
    }
  },


  // Stalls
  getStalls: async () => {
    if (!isSupabaseConfigured()) {
      return mockApi.getStalls();
    }

    try {
      const { data, error } = await (supabase as any)
        .from('stalls')
        .select('*')
        .order('stall_name', { ascending: true });

      if (error) throw error;
      return { stalls: data as Stall[] };
    } catch (error) {
      console.error('Error fetching stalls:', error);
      throw readFailed('stalls', error);
    }
  },

  createStall: async (stallData: any) => {
    if (!isSupabaseConfigured()) {
      return mockApi.createStall(stallData as any);
    }

    try {
      // Filter stallData to only include what's in the database schema
      const { stall_name, user_id, status } = stallData;
      const filteredData = {
        stall_name,
        user_id: user_id ? (typeof user_id === 'number' ? user_id : parseInt(user_id)) : null,
        status: status || 'active'
      };

      const { data, error } = await (supabase as any)
        .from('stalls')
        .insert([filteredData])
        .select()
        .single();

      if (error) throw error;
      return { stall: data as Stall };
    } catch (error) {
      console.error('Error creating stall:', error);
      throw error;
    }
  },

  updateStall: async (stallId: number, stallData: Partial<Stall>) => {
    if (!isSupabaseConfigured()) {
      return mockApi.updateStall(stallId, stallData as any);
    }

    try {
      const { data, error } = await (supabase as any)
        .from('stalls')
        .update(stallData)
        .eq('stall_id', stallId)
        .select()
        .single();

      if (error) throw error;
      return { stall: data as Stall };
    } catch (error) {
      console.error('Error updating stall:', error);
      throw error;
    }
  },

  deleteStall: async (stallId: number) => {
    if (!isSupabaseConfigured()) {
      return mockApi.deleteStall(stallId);
    }

    try {
      // Withdrawal records hold onto their stall and the database will not
      // release it, so this delete always fails once a stall has been used.
      const activity = await countStallActivity(stallId);
      if (activity.total > 0) {
        throw new Error(
          `This stall can't be deleted because it has ${describeCounts(activity.parts)} on record. ` +
          `Deleting it would break that history. Set its status to inactive instead — it stays out of the way but the records survive.`
        );
      }

      const { error } = await (supabase as any)
        .from('stalls')
        .delete()
        .eq('stall_id', stallId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error deleting stall:', error);
      throw error;
    }
  }
};

// Export as default for easy migration
export default dbApi;
